"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import type { Form } from "@/payload-types";
import { HONEYPOT_FIELD } from "@/lib/honeypot";
import { pushDataLayer } from "@/lib/gtm";
import { CSSFadeUpIn } from "@/components/animations/CSSFadeUpIn";
import { ANIMATION_DELAYS } from "@/lib/constants";
import { Plus } from "lucide-react";

interface FormField {
  id?: string | null;
  name: string;
  label?: string | null;
  blockType: string;
  required?: boolean | null;
  placeholder?: string | null;
  rows?: number | null;
  options?: Array<{ label: string; value: string }> | null;
}

interface GridContactRendererProps {
  heading?: string | null;
  description?: string | null;
  form: string | number | Form;
  submitButtonLabel?: string | null;
  sectionBackgroundColour?: string;
  hasBackgroundImage?: boolean;
  blockType: "grid-contact";
  id?: string | null;
}

const createValidationSchema = (fields: FormField[]) => {
  const schemaFields: Record<string, z.ZodType<unknown>> = {
    [HONEYPOT_FIELD]: z.string().optional(),
  };

  fields?.forEach((field) => {
    if (field.blockType === "email") {
      let schema = z.string().email("Invalid email address");
      if (field.required) {
        schema = schema.min(1, "This field is required");
      }
      schemaFields[field.name] = schema;
    } else if (field.blockType === "text" || field.blockType === "textarea") {
      let schema = z.string();
      if (field.required) {
        schema = schema.min(1, "This field is required");
      }
      schemaFields[field.name] = schema;
    } else if (field.blockType === "number") {
      let schema = z.coerce.number();
      if (field.required) {
        schema = schema.min(1, "This field is required");
      }
      schemaFields[field.name] = schema;
    }
  });

  return z.object(schemaFields);
};

const inputClasses =
  "w-full bg-transparent border border-white/30 text-white placeholder:text-white/50 px-4 h-[38px] focus:border-white focus:outline-none transition-colors";
const textareaClasses =
  "w-full bg-transparent border border-white/30 text-white placeholder:text-white/50 px-4 py-2 h-[76px] resize-none focus:border-white focus:outline-none transition-colors";

export const GridContactRenderer = (props: GridContactRendererProps) => {
  const { heading, description, form: formRelation, submitButtonLabel } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const form =
    !formRelation ||
    typeof formRelation === "string" ||
    typeof formRelation === "number"
      ? null
      : formRelation;

  const validationSchema = createValidationSchema(
    (form?.fields || []) as FormField[]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async (data: Record<string, unknown>) => {
    if (!form) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch("/api/form-submissions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          form: form.id,
          submissionData: data,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      setSubmitSuccess(true);
      reset();

      pushDataLayer("form_submit", {
        form_id: form.id,
        form_name: form.title,
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitError("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const fieldError = errors[field.name];
    const placeholder = field.placeholder || field.label || field.name;

    switch (field.blockType) {
      case "text":
        return (
          <div key={field.id}>
            <input
              {...register(field.name)}
              type="text"
              id={field.name}
              className={inputClasses}
              placeholder={placeholder}
            />
            {fieldError && (
              <p className="mt-1 text-sm text-red-400">
                {fieldError.message as string}
              </p>
            )}
          </div>
        );

      case "email":
        return (
          <div key={field.id}>
            <input
              {...register(field.name)}
              type="email"
              id={field.name}
              className={inputClasses}
              placeholder={placeholder}
            />
            {fieldError && (
              <p className="mt-1 text-sm text-red-400">
                {fieldError.message as string}
              </p>
            )}
          </div>
        );

      case "textarea":
        return (
          <div key={field.id}>
            <textarea
              {...register(field.name)}
              id={field.name}
              className={textareaClasses}
              placeholder={placeholder}
            />
            {fieldError && (
              <p className="mt-1 text-sm text-red-400">
                {fieldError.message as string}
              </p>
            )}
          </div>
        );

      case "select":
        return (
          <div key={field.id}>
            <select
              {...register(field.name)}
              id={field.name}
              className={inputClasses}
            >
              <option value="" className="bg-black">
                {placeholder}
              </option>
              {field.options?.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  className="bg-black"
                >
                  {option.label}
                </option>
              ))}
            </select>
            {fieldError && (
              <p className="mt-1 text-sm text-red-400">
                {fieldError.message as string}
              </p>
            )}
          </div>
        );

      case "number":
        return (
          <div key={field.id}>
            <input
              {...register(field.name)}
              type="number"
              id={field.name}
              className={inputClasses}
              placeholder={placeholder}
            />
            {fieldError && (
              <p className="mt-1 text-sm text-red-400">
                {fieldError.message as string}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!form) {
    return (
      <div className="decorative-grid-cell relative flex flex-col h-full p-3 sm:p-4 lg:p-5">
        <p className="text-white/50">Please select a form to display.</p>
      </div>
    );
  }

  return (
    <div className="decorative-grid-cell relative flex flex-col h-full p-3 sm:p-4 lg:p-5">
      {heading && (
        <CSSFadeUpIn delay={ANIMATION_DELAYS.FAST}>
          <h2 className="font-subheading text-white text-2xl lg:text-3xl 2xl:text-4xl mb-4">
            {heading}
          </h2>
        </CSSFadeUpIn>
      )}

      {description && (
        <CSSFadeUpIn delay={ANIMATION_DELAYS.MEDIUM}>
          <p className="text-white/80 text-sm lg:text-base 2xl:text-lg mb-8">
            {description}
          </p>
        </CSSFadeUpIn>
      )}

      {submitSuccess && (
        <div className="mb-4 p-4 border border-green-500/50 text-green-400">
          <p>Thank you! Your message has been sent successfully.</p>
        </div>
      )}

      {submitError && (
        <div className="mb-4 p-4 border border-red-500/50 text-red-400">
          <p>{submitError}</p>
        </div>
      )}

      <CSSFadeUpIn delay={ANIMATION_DELAYS.SLOW}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Honeypot field */}
          <input
            {...register(HONEYPOT_FIELD)}
            type="text"
            className="absolute -left-[9999px] opacity-0 h-0 w-0"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          {form.fields?.map((field) => {
            if (!field || typeof field !== "object" || !("name" in field))
              return null;
            return renderField(field as FormField);
          })}

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 border border-white/50 text-white px-6 py-3 hover:border-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? "Sending..."
                : (submitButtonLabel || "Send Message")}
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </form>
      </CSSFadeUpIn>
    </div>
  );
};
